/**
 * Centralized Anchor Scroll Management
 * Single source of truth for ALL anchor link scrolling across the site
 */

import { BREAKPOINTS } from './constants.js';

/**
 * Feature sections that appear after the jump-links-nav on index.html
 */
const FEATURE_SECTIONS = ['setup', 'collect', 'analyse', 'share'];

/**
 * Check if current page is index.html
 */
function isIndexPage() {
  const pathname = window.location.pathname;
  return pathname === '/' || pathname === '/index.html' || pathname.endsWith('/');
}

/**
 * Check if target is a feature section on index.html
 */
function isFeatureSection(targetId) {
  return FEATURE_SECTIONS.includes(targetId);
}

/**
 * Calculate the scroll offset for a given target element
 * @param {string} targetId - The ID of the target element (without #)
 * @param {Element|null} targetElement - The target DOM element
 * @returns {number} - Total offset to subtract from scroll position
 */
export function calculateScrollOffset(targetId, targetElement = null) {
  const header = document.querySelector('.header');
  const jumpLinksNav = document.querySelector('#jumpLinksNav');

  let totalOffset = 0;

  // Always include header height
  const headerHeight = header ? header.offsetHeight : 80; // Default to 80px if not found
  totalOffset += headerHeight;

  // Include jump-links-nav height only for feature sections on index.html
  if (isIndexPage() && isFeatureSection(targetId) && jumpLinksNav) {
    const navHeight = jumpLinksNav.offsetHeight || 80; // Default to 80px if not measured
    totalOffset += navHeight;
  }

  // Add buffer for spacing (larger on mobile)
  const isMobile = window.innerWidth < BREAKPOINTS.tablet;
  const buffer = isMobile ? 120 : 20;
  totalOffset += buffer;

  return totalOffset;
}

/**
 * Scroll to an anchor target with proper offset
 * @param {string} targetId - The ID of the target element (without #)
 * @param {Object} options - Scroll options
 * @param {string} options.behavior - Scroll behavior ('smooth' or 'auto')
 */
export function scrollToAnchor(targetId, options = {}) {
  const { behavior = 'smooth' } = options;

  const targetElement = document.getElementById(targetId);

  if (!targetElement) {
    console.warn(`Anchor target not found: #${targetId}`);
    return;
  }

  const offset = calculateScrollOffset(targetId, targetElement);
  const targetPosition = targetElement.offsetTop - offset;

  window.scrollTo({
    top: Math.max(0, targetPosition), // Ensure we don't scroll to negative position
    behavior: behavior
  });
}

/**
 * Handle hash navigation on page load
 */
export function handleHashOnPageLoad() {
  const hash = window.location.hash;
  if (!hash || hash.length <= 1) return;

  const targetId = hash.substring(1); // Remove # prefix

  // Small delay to ensure DOM is fully ready
  setTimeout(() => {
    scrollToAnchor(targetId);
  }, 100);
}

/**
 * Setup global anchor link handlers
 * Handles both same-page (#anchor) and cross-page (/#anchor) navigation
 */
export function setupAnchorLinkHandlers() {
  // Handle all anchor links (both #anchor and /#anchor formats)
  const anchorLinks = document.querySelectorAll('a[href^="#"], a[href^="/#"]');

  anchorLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      if (!href || href === '#') return;

      // Extract target ID
      let targetId;
      if (href.startsWith('/#')) {
        targetId = href.substring(2); // Remove /# prefix
      } else if (href.startsWith('#')) {
        targetId = href.substring(1); // Remove # prefix
      }

      if (!targetId) return;

      // Check if this is a cross-page link (goes to index.html)
      const isCrossPageLink = href.startsWith('/#');

      if (isCrossPageLink) {
        // Check if we're already on index.html
        if (isIndexPage()) {
          // Same page navigation - prevent default and scroll
          e.preventDefault();
          scrollToAnchor(targetId);
        } else {
          // Different page - allow navigation, hash handler will take over
          // Don't preventDefault, let browser navigate with hash
        }
      } else {
        // Same-page anchor link (#anchor)
        e.preventDefault();
        scrollToAnchor(targetId);
      }
    });
  });

  console.log(`Anchor scroll handlers initialized for ${anchorLinks.length} links`);
}

/**
 * Initialize the anchor scroll management system
 * Call this once when the page loads
 */
export function initAnchorScroll() {
  // Setup click handlers for all anchor links
  setupAnchorLinkHandlers();

  // Handle hash if present in URL on page load
  handleHashOnPageLoad();

  console.log('Anchor scroll system initialized');
}
